import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'
import { WoodChips } from '../shared/WoodChips'

type Phase = 'float' | 'held' | 'fly' | 'respawn'

/** ⑥ 手渡しの一片 — 目の前の木片をドラッグで掴んで観察し、放すと三鷹へ飛んでいく */
export class Proto6 extends ProtoBase {
  readonly title = '6. 手渡しの一片'
  readonly desc = '大徳寺五老松の板。ドラッグで回して裏の墨書を見る。放すと飛んでいく'

  private chip!: THREE.Mesh
  private phase: Phase = 'float'
  private phaseT = 0
  private dragging = false
  private lastX = 0
  private lastY = 0
  private ray = new THREE.Raycaster()
  private ndc = new THREE.Vector2()
  private flyFrom = new THREE.Vector3()
  private onDown = (e: PointerEvent) => this.pointerDown(e)
  private onMove = (e: PointerEvent) => this.pointerMove(e)
  private onUp = () => this.pointerUp()

  protected start(): void {
    this.ctx.setWorld('void')
    this.ctx.controls.enabled = false
    this.ctx.view.setPose(new THREE.Vector3(0, 1.5, 1.6), new THREE.Vector3(0, 1.4, 0))

    // 板（表=木目、裏=墨書）
    const front = WoodChips.makeChipTexture()
    const back = WoodChips.makeChipTexture('五老松')
    const mats = [
      new THREE.MeshStandardMaterial({ map: front, roughness: 0.7 }),
      new THREE.MeshStandardMaterial({ map: front, roughness: 0.7 }),
      new THREE.MeshStandardMaterial({ map: front, roughness: 0.7 }),
      new THREE.MeshStandardMaterial({ map: back, roughness: 0.7 }),
      new THREE.MeshStandardMaterial({ map: front, roughness: 0.7 }),
      new THREE.MeshStandardMaterial({ map: back, roughness: 0.7 }),
    ]
    this.chip = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.045, 0.3), mats)
    this.chip.position.set(0, 1.4, 0)
    this.group.add(this.chip)

    const spot = new THREE.PointLight(0xffe0b0, 12, 8)
    spot.position.set(0.8, 2.4, 1.2)
    this.group.add(spot)

    this.ctx.setCaption('手に取ってみよ——（ドラッグで回す）京・大徳寺「五老松の板」')

    const dom = this.ctx.renderer.domElement
    dom.addEventListener('pointerdown', this.onDown)
    dom.addEventListener('pointermove', this.onMove)
    window.addEventListener('pointerup', this.onUp)
  }

  private pointerDown(e: PointerEvent): void {
    if (this.phase !== 'float' && this.phase !== 'held') return
    this.ndc.set((e.clientX / window.innerWidth) * 2 - 1, -(e.clientY / window.innerHeight) * 2 + 1)
    this.ray.setFromCamera(this.ndc, this.ctx.camera)
    if (this.ray.intersectObject(this.chip).length > 0) {
      this.dragging = true
      this.phase = 'held'
      this.phaseT = 0
      this.lastX = e.clientX
      this.lastY = e.clientY
      this.ctx.setCaption('裏に、来歴の墨書——望月らの縁が、木に残っている')
    }
  }

  private pointerMove(e: PointerEvent): void {
    if (!this.dragging) return
    const dx = e.clientX - this.lastX
    const dy = e.clientY - this.lastY
    this.lastX = e.clientX
    this.lastY = e.clientY
    this.chip.rotation.y += dx * 0.012
    this.chip.rotation.x += dy * 0.012
  }

  private pointerUp(): void {
    if (!this.dragging) return
    this.dragging = false
    this.phase = 'fly'
    this.phaseT = 0
    this.flyFrom.copy(this.chip.position)
    this.ctx.sfx.play('chime')
    this.ctx.setCaption('三鷹へ——')
  }

  update(dt: number): void {
    this.elapsed += dt
    this.phaseT += dt
    switch (this.phase) {
      case 'float':
        this.chip.position.y = 1.4 + Math.sin(this.elapsed * 1.3) * 0.03
        this.chip.rotation.y += dt * 0.25
        // VRではドラッグ不可のため自動で観察→飛去サイクル
        if (this.ctx.view.xrActive && this.phaseT > 6) {
          this.phase = 'fly'
          this.phaseT = 0
          this.flyFrom.copy(this.chip.position)
          this.ctx.sfx.play('chime')
          this.ctx.setCaption('三鷹へ——')
        }
        break
      case 'held':
        break
      case 'fly': {
        const u = Math.min(1, this.phaseT / 1.6)
        const a = 1 - u
        const p1 = new THREE.Vector3(1.2, 3.2, -4)
        const p2 = new THREE.Vector3(7, 9, -26)
        this.chip.position
          .copy(this.flyFrom)
          .multiplyScalar(a * a)
          .addScaledVector(p1, 2 * a * u)
          .addScaledVector(p2, u * u)
        this.chip.rotation.z += dt * 6
        if (u >= 1) {
          this.phase = 'respawn'
          this.phaseT = 0
        }
        break
      }
      case 'respawn':
        if (this.phaseT > 1.4) {
          this.phase = 'float'
          this.chip.position.set(0, 1.4, 0)
          this.chip.rotation.set(0, 0, 0)
          this.ctx.setCaption('手に取ってみよ——（ドラッグで回す）京・大徳寺「五老松の板」')
        }
        break
    }
  }

  protected onDispose(): void {
    const dom = this.ctx.renderer.domElement
    dom.removeEventListener('pointerdown', this.onDown)
    dom.removeEventListener('pointermove', this.onMove)
    window.removeEventListener('pointerup', this.onUp)
    this.ctx.controls.enabled = true
  }
}
